import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
  LEGACY_HUB_MIGRATION_ERROR_CODES,
  LEGACY_HUB_MIGRATION_PHASES,
  type LegacyHubMigrationPhaseId,
  type LegacyHubMigrationStatusResponse,
  type LegacyHubMigrationSummary,
  type TaskRecord,
} from "@nexus/contracts"

export interface LegacyHubMigrationDialogProps {
  /** The tenant this run migrates: the active tenant. Shown so the admin confirms the right one. */
  tenantId: string
  tenantName: string | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  preview: LegacyHubMigrationSummary | undefined
  isPreviewLoading: boolean
  isPreviewError: boolean
  status: LegacyHubMigrationStatusResponse | undefined
  /** True once this dialog started a run, so it follows that run instead of the preview. */
  hasStarted: boolean
  isStarting: boolean
  onConfirm: () => void
}

type CopyKey = "sets" | "opportunities" | "scripts" | "scenes" | "products"
type RemapKey = keyof LegacyHubMigrationSummary["remap"]

const COPY_ROWS: ReadonlyArray<{ key: CopyKey; phase: LegacyHubMigrationPhaseId }> = [
  { key: "sets", phase: "sets" },
  { key: "opportunities", phase: "opportunities" },
  { key: "scripts", phase: "scripts" },
  { key: "scenes", phase: "scenes" },
  { key: "products", phase: "joinRows" },
]
const REMAP_ROWS: readonly RemapKey[] = ["creativeFlags", "marketplaceOrderScripts", "shareLinks"]
const PERCENT = 100

function phaseLabel(t: TFunction, phase: LegacyHubMigrationPhaseId): string {
  const fallback = LEGACY_HUB_MIGRATION_PHASES.find((row) => row.id === phase)?.label ?? phase
  return t(`opportunities.legacyHubMigration.phases.${phase}`, fallback)
}

function remapLabel(t: TFunction, key: RemapKey): string {
  const fallbacks: Record<RemapKey, string> = {
    creativeFlags: "Creative flags",
    marketplaceOrderScripts: "Sent scripts",
    shareLinks: "Share links",
  }
  return t(`opportunities.legacyHubMigration.remapRows.${key}`, fallbacks[key])
}

/** The run stores a stable code; the raw error stays in the worker logs. */
function failedRunMessage(t: TFunction, code: string): string {
  if (code === LEGACY_HUB_MIGRATION_ERROR_CODES.NOT_STARTED) {
    return t(
      "opportunities.legacyHubMigration.failedNotStarted",
      "The last migration never started. It is safe to run it again.",
    )
  }
  if (code === LEGACY_HUB_MIGRATION_ERROR_CODES.INTERRUPTED) {
    return t(
      "opportunities.legacyHubMigration.failedInterrupted",
      "The last migration stopped before it finished. It is safe to run it again.",
    )
  }
  return t("opportunities.legacyHubMigration.failedGeneric", "The last migration failed. It is safe to run it again.")
}

const COPY_KEYS = ["sets", "products", "opportunities", "customOpportunities", "scripts", "scenes"] as const

/** False when a run would copy, fill in, or remap nothing. */
function hasWorkToDo(preview: LegacyHubMigrationSummary): boolean {
  const copies = COPY_KEYS.some((key) => preview.copy[key].wouldInsert > 0)
  const remaps = REMAP_ROWS.some((key) => preview.remap[key].remapped > 0)
  const fills = preview.copy.backReferencesFilled > 0 || preview.copy.scriptAssetsFilled > 0
  return copies || remaps || fills
}

/** The hidden "Your Scripts" opportunities, listed apart so the Opportunities line matches the set cards. */
function customOpportunitiesLabel(t: TFunction): string {
  return t("opportunities.legacyHubMigration.customOpportunities", "Hidden “Your Scripts” opportunities")
}

function CountRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </li>
  )
}

function PreviewCounts({ preview, t }: { preview: LegacyHubMigrationSummary; t: TFunction }) {
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-1.5" aria-label={t("opportunities.legacyHubMigration.copyHeading", "Copy to hub")}>
        {COPY_ROWS.map(({ key, phase }) => (
          <CountRow
            key={key}
            label={phaseLabel(t, phase)}
            value={t("opportunities.legacyHubMigration.previewCopy", {
              toCopy: preview.copy[key].wouldInsert,
              existing: preview.copy[key].alreadyAnchored,
              defaultValue: "{{toCopy}} to copy, {{existing}} already in hub",
            })}
          />
        ))}
        {preview.copy.customOpportunities.source > 0 ? (
          <CountRow
            label={customOpportunitiesLabel(t)}
            value={t("opportunities.legacyHubMigration.previewCopy", {
              toCopy: preview.copy.customOpportunities.wouldInsert,
              existing: preview.copy.customOpportunities.alreadyAnchored,
              defaultValue: "{{toCopy}} to copy, {{existing}} already in hub",
            })}
          />
        ) : null}
      </ul>
      <ul className="flex flex-col gap-1.5" aria-label={t("opportunities.legacyHubMigration.remapHeading", "Point at hub")}>
        {REMAP_ROWS.map((key) => (
          <CountRow
            key={key}
            label={remapLabel(t, key)}
            value={t("opportunities.legacyHubMigration.previewRemap", {
              count: preview.remap[key].remapped,
              defaultValue: "{{count}} to point at hub",
            })}
          />
        ))}
      </ul>
    </div>
  )
}

function SummaryCounts({ summary, t }: { summary: LegacyHubMigrationSummary; t: TFunction }) {
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-1.5" aria-label={t("opportunities.legacyHubMigration.copiedHeading", "Copied to hub")}>
        {COPY_ROWS.map(({ key, phase }) => (
          <CountRow
            key={key}
            label={phaseLabel(t, phase)}
            value={t("opportunities.legacyHubMigration.summaryCopy", {
              copied: summary.copy[key].wouldInsert,
              skipped: summary.copy[key].alreadyAnchored,
              defaultValue: "{{copied}} copied, {{skipped}} already in hub",
            })}
          />
        ))}
        {summary.copy.customOpportunities.source > 0 ? (
          <CountRow
            label={customOpportunitiesLabel(t)}
            value={t("opportunities.legacyHubMigration.summaryCopy", {
              copied: summary.copy.customOpportunities.wouldInsert,
              skipped: summary.copy.customOpportunities.alreadyAnchored,
              defaultValue: "{{copied}} copied, {{skipped}} already in hub",
            })}
          />
        ) : null}
      </ul>
      <ul className="flex flex-col gap-1.5" aria-label={t("opportunities.legacyHubMigration.remappedHeading", "Pointed at hub")}>
        {REMAP_ROWS.map((key) => (
          <CountRow
            key={key}
            label={remapLabel(t, key)}
            value={t("opportunities.legacyHubMigration.summaryRemap", {
              count: summary.remap[key].remapped,
              defaultValue: "{{count}} remapped",
            })}
          />
        ))}
      </ul>
    </div>
  )
}

function readCount(metadata: Record<string, unknown> | undefined, key: string): number {
  const value = metadata?.[key]
  return typeof value === "number" ? value : 0
}

function PhaseProgress({ task, t }: { task: TaskRecord | null; t: TFunction }) {
  const subtasks = task?.stages[0]?.subtasks ?? []
  return (
    <ul className="flex flex-col gap-2.5" aria-label={t("opportunities.legacyHubMigration.progressHeading", "Migration progress")}>
      {LEGACY_HUB_MIGRATION_PHASES.map(({ id }) => {
        const subtask = subtasks.find((row) => row.id === id)
        const percent = Math.round((subtask?.progress ?? 0) * PERCENT)
        const total = readCount(subtask?.metadata, "total")
        return (
          <li key={id} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-4 text-sm">
              <span className={subtask?.hasErrors ? "text-destructive" : undefined}>{phaseLabel(t, id)}</span>
              <span className="tabular-nums text-muted-foreground">
                {id !== "remap" && total > 0
                  ? t("opportunities.legacyHubMigration.phaseCounts", {
                      done: readCount(subtask?.metadata, "copied") + readCount(subtask?.metadata, "skipped"),
                      total,
                      defaultValue: "{{done}} of {{total}}",
                    })
                  : t("opportunities.legacyHubMigration.phasePercent", { percent, defaultValue: "{{percent}}%" })}
              </span>
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={PERCENT}
              aria-valuenow={percent}
              aria-label={phaseLabel(t, id)}
            >
              <div
                className={subtask?.hasErrors ? "h-full bg-destructive" : "h-full bg-primary transition-[width]"}
                style={{ width: `${percent}%` }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/** Dry-run preview → confirm → live phase progress → summary or error (AD-3547). */
export function LegacyHubMigrationDialog({
  tenantId,
  tenantName,
  open,
  onOpenChange,
  preview: loadedPreview,
  isPreviewLoading,
  isPreviewError,
  status,
  hasStarted,
  isStarting,
  onConfirm,
}: LegacyHubMigrationDialogProps) {
  const { t } = useTranslation()
  // A preview still loading may be the last one cached, from before a run that has since finished.
  const preview = isPreviewLoading ? undefined : loadedPreview
  const result = status?.result ?? null
  const isRunning = status?.running ?? false
  const showProgress = isRunning || (hasStarted && result?.status !== "COMPLETED" && result?.status !== "FAILED")
  const completedSummary = result?.status === "COMPLETED" ? result.summary : undefined
  const finishedSummary = hasStarted ? completedSummary : undefined
  // Reopened after an earlier run: show what it did above the fresh preview, which still allows a re-run.
  const lastRunSummary = hasStarted ? undefined : completedSummary
  const lastError = result?.status === "FAILED" ? (result.error ?? "") : undefined
  const hasWork = preview ? hasWorkToDo(preview) : false
  const canConfirm = !showProgress && !finishedSummary && hasWork && !isStarting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("opportunities.legacyHubMigration.dialogTitle", "Migrate legacy opportunities to hub")}</DialogTitle>
          <DialogDescription>
            {t(
              "opportunities.legacyHubMigration.dialogDescription",
              "Copies this tenant's legacy sets, opportunities, scripts and scenes into the opportunity hub, then points creative flags, sent scripts and share links at the copies. Legacy data is not changed, and running it again skips what was already copied.",
            )}
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-md border bg-muted/40 p-3 text-sm">
          {tenantName
            ? t("opportunities.legacyHubMigration.tenantLine", {
                name: tenantName,
                id: tenantId,
                defaultValue: "Tenant: {{name}} ({{id}})",
              })
            : t("opportunities.legacyHubMigration.tenantIdLine", { id: tenantId, defaultValue: "Tenant: {{id}}" })}
        </p>

        {lastError !== undefined && !showProgress ? (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {failedRunMessage(t, lastError)}
          </p>
        ) : null}

        {lastRunSummary && !showProgress ? (
          <div className="flex flex-col gap-2 border-b pb-3">
            <p className="text-sm font-medium">
              {t("opportunities.legacyHubMigration.lastCompleted", "The last migration completed")}
            </p>
            <SummaryCounts summary={lastRunSummary} t={t} />
          </div>
        ) : null}

        {showProgress ? (
          <PhaseProgress task={status?.task ?? null} t={t} />
        ) : finishedSummary ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{t("opportunities.legacyHubMigration.completed", "Migration complete")}</p>
            <SummaryCounts summary={finishedSummary} t={t} />
          </div>
        ) : isPreviewLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="loader" size="xs" color="inherit" className="animate-spin" />
            {t("opportunities.legacyHubMigration.previewLoading", "Counting legacy data…")}
          </div>
        ) : isPreviewError ? (
          <p role="alert" className="text-sm text-destructive">
            {t("opportunities.legacyHubMigration.previewError", "Could not count this tenant's legacy data. Try again.")}
          </p>
        ) : preview ? (
          <div className="flex flex-col gap-2">
            {lastRunSummary ? (
              <p className="text-sm font-medium">
                {t("opportunities.legacyHubMigration.runAgainHeading", "If you run it again")}
              </p>
            ) : null}
            <PreviewCounts preview={preview} t={t} />
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {showProgress || finishedSummary ? t("common.close", "Close") : t("common.cancel")}
          </Button>
          {showProgress ? (
            <Button type="button" disabled>
              <Icon name="loader" size="xs" color="inherit" className="mr-1.5 animate-spin" />
              {t("opportunities.legacyHubMigration.migrating", "Migrating…")}
            </Button>
          ) : finishedSummary ? null : (
            <Button type="button" onClick={onConfirm} disabled={!canConfirm}>
              {isStarting && <Icon name="loader" size="xs" color="inherit" className="mr-1.5 animate-spin" />}
              {preview && !hasWork
                ? t("opportunities.legacyHubMigration.nothingToMigrate", "Nothing to migrate")
                : t("opportunities.legacyHubMigration.confirm", "Migrate")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
